import React, { FC, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import CustomGroupPicker from 'common/typeaheads/GroupPicker/CustomGroupPicker';
import {
  CustomFieldDefinitionEntityTypeModel as OcCustomFieldDefinitionEntityTypeModel,
  CustomFieldDefinitionGroupModel as OcCustomFieldDefinitionGroupModel
} from '@utmost.co/enterprise-models';
import { ChipsSelect, Input, Select } from '@beeline.com/bp-design-system';
import {
  CustomFieldDefinitionEntityTypeModel,
  CustomFieldDefinitionModelInput,
  CustomFieldDefinitionGroupModel,
  CustomFieldDefinitionEvaluationTypeModel,
  CustomFieldDefinitionDataTypeModel
} from 'src/graphql/types';
import { filterNotEmpty } from 'src/utils/data/listHelpers';
import { useTypedQuery } from 'src/utils/hooks/useTypedQuery';
import {
  CustomFieldFormDataTest,
  booleanOptions,
  customFieldDataTypeOptions,
  entityTypesOptions
} from '../utils/CustomFieldsDefinitions.constants';
import { useCustomFieldsAndObjectsIntl } from '../../translations/useCustomFieldsAndObjectsIntl';
import { isCodeValid, objectToOptions } from '../utils/CustomFieldDefinitions.utils';
import { GetSimpleObjectsDocument } from '../utils/__generated__/GetSimpleObjects.graphql';
import { CustomFieldCostCenterValuesForm } from './CustomFieldCostCenterValuesForm';
import { AceEditor } from 'src/components/common/AceEditor/AceEditor';

export interface SaveCustomFieldFormProps {
  onCustomFieldChange: (customFieldDefinition: CustomFieldDefinitionModelInput) => void;
  customFieldDefinition: CustomFieldDefinitionModelInput | undefined;
  isChildOfObject?: boolean;
  isEditing?: boolean;
}

export const SaveCustomFieldForm: FC<React.PropsWithChildren<SaveCustomFieldFormProps>> = ({
  isChildOfObject,
  onCustomFieldChange,
  isEditing,
  customFieldDefinition
}) => {
  const intl = useCustomFieldsAndObjectsIntl();
  const isEditMode = !customFieldDefinition?.uid;

  const { data: simpleObjects, loading, get: getSimpleObjects } = useTypedQuery(GetSimpleObjectsDocument);
  const hasValidCode = isCodeValid(customFieldDefinition?.code) || false;

  // biome-ignore lint/correctness/useExhaustiveDependencies: exception
  useEffect(() => {
    if (customFieldDefinition?.dataType === 'OBJECT') {
      getSimpleObjects();
    }
    // exclude getSimpleObjects to avoid unnecessary re-renders
  }, [customFieldDefinition?.dataType]);

  const objectOptions = useMemo(
    () => objectToOptions(simpleObjects?.customObjectDefinitions?.customObjectDefinitions),
    [simpleObjects?.customObjectDefinitions?.customObjectDefinitions]
  );

  const enumValuesList = useMemo(
    () => (filterNotEmpty(customFieldDefinition?.values?.map((value) => value?.value)) ?? []).join('\n'),
    [customFieldDefinition?.values]
  );

  const showMultiValued = customFieldDefinition?.dataType === 'OBJECT' || customFieldDefinition?.dataType === 'ENUM';
  const typeOptions = customFieldDataTypeOptions.filter((type) => type.value !== 'COST_CENTER');

  return (
    <StyledForm>
      {!isChildOfObject && (
        <ChipsSelect<CustomFieldDefinitionEntityTypeModel | null>
          required
          disabled={isEditing}
          dataTest={CustomFieldFormDataTest.ParentEntityType}
          onSelect={(value) => (value ? onCustomFieldChange({ parentEntityType: value }) : {})}
          value={customFieldDefinition?.parentEntityType}
          label={intl.entityType}
          options={entityTypesOptions}
        />
      )}
      <Input
        dataTest={CustomFieldFormDataTest.Name}
        label={intl.nameLabel}
        required
        fullWidth
        value={customFieldDefinition?.name ?? ''}
        onChange={(e) => onCustomFieldChange({ name: e.target.value })}
      />
      <Input
        label={intl.codeLabel}
        dataTest={CustomFieldFormDataTest.Code}
        fullWidth
        required
        value={customFieldDefinition?.code ?? ''}
        onChange={(e) => onCustomFieldChange({ code: e.target.value })}
        error={!hasValidCode}
        errorText={
          customFieldDefinition?.code && !hasValidCode
            ? 'Code can only contain alphanumeric characters, numbers, and hash, space, underscore and dash characters'
            : undefined
        }
      />
      {!isChildOfObject && (
        <CustomGroupPicker
          dataTest={CustomFieldFormDataTest.Group}
          label={intl.groupLabel}
          entityType={customFieldDefinition?.parentEntityType as OcCustomFieldDefinitionEntityTypeModel}
          onChange={(group) => onCustomFieldChange({ group: group as CustomFieldDefinitionGroupModel })}
          value={customFieldDefinition?.group as OcCustomFieldDefinitionGroupModel}
        />
      )}
      <Input
        dataTest={CustomFieldFormDataTest.HelperText}
        fullWidth
        label={intl.helperTextLabel}
        value={customFieldDefinition?.helperText ?? ''}
        onChange={(e) => onCustomFieldChange({ helperText: e.target.value })}
      />
      <Select<CustomFieldDefinitionDataTypeModel>
        dataTest={CustomFieldFormDataTest.Type}
        label={intl.typeLabel}
        options={typeOptions}
        valueOptions={typeOptions.find((option) => option.value === customFieldDefinition?.dataType)}
        required
        disabled={!isEditMode}
        autoComplete
        onOptionChange={(val) => {
          if (val) {
            onCustomFieldChange({
              dataType: val.value
            });
          }
        }}
      />
      {customFieldDefinition?.dataType === 'ENUM' && (
        <AceEditor
          label={intl.valuesLabel}
          dataTest={CustomFieldFormDataTest.Values}
          value={enumValuesList}
          helperText={intl.valuesHelperText}
          onChange={(newValue) =>
            onCustomFieldChange({
              values: newValue.split('\n').map((value) => ({ label: value, value }))
            })
          }
          enableLiveAutocompletion={false}
          enableBasicAutocompletion={false}
          mode="text"
          minLines={5}
          maxLines={26}
        />
      )}
      {customFieldDefinition?.dataType === 'OBJECT' && (
        <Select<string>
          label={intl.objectLabel}
          dataTest={CustomFieldFormDataTest.ObjectType}
          options={objectOptions}
          loading={loading}
          helperText={intl.objectHelperText}
          placeholder={intl.objectPlaceholder}
          autoComplete
          valueOptions={
            customFieldDefinition?.customObjectDefinition?.name && customFieldDefinition?.customObjectDefinition.uid
              ? {
                  label: customFieldDefinition.customObjectDefinition.name,
                  value: customFieldDefinition.customObjectDefinition.uid
                }
              : undefined
          }
          onOptionChange={(customObject) => {
            if (customObject) {
              onCustomFieldChange({
                customObjectDefinition: simpleObjects?.customObjectDefinitions?.customObjectDefinitions?.find(
                  (obj) => obj?.uid === customObject.value
                )
              });
            }
          }}
        />
      )}
      {!isChildOfObject && showMultiValued && (
        <ChipsSelect<boolean>
          label={intl.multivaluedLabel}
          dataTest={CustomFieldFormDataTest.MultiValued}
          options={booleanOptions}
          helperText={intl.multiValuedHelperText}
          disabled={!isEditMode}
          value={!!customFieldDefinition?.multivalued}
          onSelect={(multivalued) => onCustomFieldChange({ multivalued })}
        />
      )}
      {customFieldDefinition?.dataType === 'COST_CENTER' && (
        <CustomFieldCostCenterValuesForm
          onCustomFieldChange={onCustomFieldChange}
          customFieldDefinition={customFieldDefinition}
        />
      )}
      {(customFieldDefinition?.dataType === 'INTEGER' || customFieldDefinition?.dataType === 'STRING') && (
        <Input
          label={intl.calculatedFieldsLabel}
          dataTest={CustomFieldFormDataTest.Calculated}
          fullWidth
          helperText={intl.calculatedFieldsHelperText}
          value={customFieldDefinition?.calculation?.expression ?? ''}
          onChange={(e) =>
            onCustomFieldChange({
              ...customFieldDefinition,
              calculation: {
                evaluationType: CustomFieldDefinitionEvaluationTypeModel.OnRead,
                expression: e.target.value
              }
            })
          }
        />
      )}
    </StyledForm>
  );
};

const StyledForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacingSize.l};
`;
